import { useCallback, useEffect, useRef, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { Disposable } from "relay-runtime"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { toast } from "@/hooks/useToast"
import { formatDate } from "@/lib/format-date"
import { ExportScopeChips } from "@/modules/patients/detail/record-extract/components/export-scope-chips"
import { ExtractFilters } from "@/modules/patients/detail/record-extract/components/extract-filters"
import { ExtractPreviewPane } from "@/modules/patients/detail/record-extract/components/extract-preview-pane"
import { buildExportInput } from "@/modules/patients/detail/record-extract/lib/build-export-input"
import {
  recordExtractFormDefaultValues,
  recordExtractFormSchema,
  RecordExtractFormValues,
} from "@/modules/patients/detail/record-extract/lib/record-extract-schema"
import { useConfirmPatientRecordExportMutation } from "@/modules/patients/detail/record-extract/services/useConfirmPatientRecordExportMutation"
import {
  PatientRecordExportRequestChange,
  usePatientRecordExportRequestUpdatedSubscription,
} from "@/modules/patients/detail/record-extract/services/usePatientRecordExportRequestUpdatedSubscription"
import { useRequestPatientRecordExportMutation } from "@/modules/patients/detail/record-extract/services/useRequestPatientRecordExportMutation"
import {
  EpisodeOfCareOption,
  EXPORT_SCOPE,
  PREVIEW_ERROR_KEY,
  PREVIEW_STAGE,
  PREVIEW_STATUS,
  PreviewErrorKey,
  PreviewStage,
  PreviewStatus,
} from "@/modules/patients/detail/record-extract/types/record-extract-types"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { useConfirmStore } from "@/stores/useConfirmStore"

interface RecordExtractDialogProps {
  patient: PatientFragment$data
  episodeOfCareOptions: EpisodeOfCareOption[]
  onClose: VoidFunction
}

export const RecordExtractDialog = ({ patient, episodeOfCareOptions, onClose }: RecordExtractDialogProps) => {
  const { t } = useTranslation()
  const confirm = useConfirmStore((state) => state.confirm)
  const hasNoContact =
    patient.contactChannel === "NO_CHANNEL" || (patient.contactChannel === "APP" && !patient.isContactChannelVerified)
  const [previewStatus, setPreviewStatus] = useState<PreviewStatus>(
    hasNoContact ? PREVIEW_STATUS.error : PREVIEW_STATUS.idle,
  )
  const [requestId, setRequestId] = useState<string | null>(null)
  const [summaryFileId, setSummaryFileId] = useState<string | null>(null)
  const [errorMessageKey, setErrorMessageKey] = useState<PreviewErrorKey>(
    hasNoContact ? PREVIEW_ERROR_KEY.noContact : PREVIEW_ERROR_KEY.generic,
  )
  const [stage, setStage] = useState<PreviewStage>(PREVIEW_STAGE.fetching)

  const [requestExport] = useRequestPatientRecordExportMutation()
  const [confirmExport, isConfirming] = useConfirmPatientRecordExportMutation()
  const mutationDisposableRef = useRef<Disposable>(undefined)

  const failPreview = useCallback((key: PreviewErrorKey = PREVIEW_ERROR_KEY.generic) => {
    setErrorMessageKey(key)
    setPreviewStatus(PREVIEW_STATUS.error)
  }, [])

  const onExportChange = useCallback(
    (change: PatientRecordExportRequestChange) => {
      switch (change.status) {
        case "GENERATING_SUMMARY":
          setStage(change.appointmentsFetched ? PREVIEW_STAGE.generating : PREVIEW_STAGE.fetching)
          break
        case "SUMMARY_READY":
          setSummaryFileId(change.summaryFileId)
          setPreviewStatus(PREVIEW_STATUS.loaded)
          break
        case "FAILED":
          failPreview()
          break
        case "DISCARDED":
          failPreview(PREVIEW_ERROR_KEY.expired)
          break
        case "PACKAGING":
        case "DELIVERING":
        case "COMPLETED":
          break
      }
    },
    [failPreview],
  )

  const { subscribe, dispose } = usePatientRecordExportRequestUpdatedSubscription(onExportChange, failPreview)

  useEffect(() => () => dispose(), [dispose])

  const form = useForm<RecordExtractFormValues>({
    mode: "all",
    resolver: zodResolver(recordExtractFormSchema),
    defaultValues: recordExtractFormDefaultValues,
  })
  const { control, handleSubmit } = form
  const watchedValues = useWatch({ control })
  const [generatedSnapshot, setGeneratedSnapshot] = useState<string | null>(null)

  const isCustomScope = watchedValues.scope === EXPORT_SCOPE.custom

  const isPreviewStale =
    previewStatus === PREVIEW_STATUS.loaded &&
    generatedSnapshot !== null &&
    JSON.stringify(buildExportInput(patient.id, form.getValues())) !== generatedSnapshot
  const displayStatus = isPreviewStale ? PREVIEW_STATUS.stale : previewStatus

  const handleGeneratePreview = (values: RecordExtractFormValues) => {
    mutationDisposableRef.current?.dispose()
    dispose()
    setGeneratedSnapshot(JSON.stringify(buildExportInput(patient.id, values)))
    setSummaryFileId(null)
    setErrorMessageKey(PREVIEW_ERROR_KEY.generic)
    setStage(PREVIEW_STAGE.fetching)
    setPreviewStatus(PREVIEW_STATUS.generating)

    mutationDisposableRef.current = requestExport({
      variables: { input: buildExportInput(patient.id, values) },
      onCompleted: (response, graphqlErrors) => {
        if (graphqlErrors?.length) {
          failPreview()
          return
        }
        const output = response.ch_requestPatientRecordExport.requestPatientRecordExportOutput
        if (!output) {
          failPreview()
          return
        }
        setRequestId(output.requestId)
        subscribe(output.requestId)
      },
      onError: () => failPreview(),
    })
  }
  const submitGeneratePreview = handleSubmit(handleGeneratePreview)

  const patientName = `${patient.lastName}, ${patient.firstName}`
  const birthDateAndDisplayId = [formatDate(new Date(patient.birthDate)), patient.displayId].filter(Boolean).join(" · ")
  const subtitle = [patientName, birthDateAndDisplayId].filter(Boolean).join(" • ")

  const handleClose = () => {
    mutationDisposableRef.current?.dispose()
    dispose()
    setPreviewStatus(PREVIEW_STATUS.idle)
    setGeneratedSnapshot(null)
    setRequestId(null)
    setSummaryFileId(null)
    setErrorMessageKey(PREVIEW_ERROR_KEY.generic)
    setStage(PREVIEW_STAGE.fetching)
    onClose()
  }

  const handleCancel = async () => {
    if (previewStatus === PREVIEW_STATUS.idle || hasNoContact) {
      handleClose()
      return
    }

    const isConfirmed = await confirm({
      title: t("patient.recordExtract.cancelConfirm.title"),
      content: t("patient.recordExtract.cancelConfirm.description"),
      cancelText: "common.button.back",
    })
    if (isConfirmed) handleClose()
  }

  const handleExportAndSend = async () => {
    if (!requestId) return

    const isConfirmed = await confirm({
      title: t("patient.recordExtract.confirmSend.title", { patientName }),
      content: t("patient.recordExtract.confirmSend.description"),
      confirmText: "common.button.confirm",
      cancelText: "common.button.back",
    })
    if (!isConfirmed) return

    mutationDisposableRef.current = confirmExport({
      variables: { input: { requestId } },
      onCompleted: (response, graphqlErrors) => {
        if (graphqlErrors?.length) {
          failPreview()
          return
        }
        if (!response.ch_confirmPatientRecordExport.confirmPatientRecordExportOutput) {
          failPreview(PREVIEW_ERROR_KEY.expired)
          return
        }
        toast({ description: t("patient.recordExtract.sendSuccess") })
        handleClose()
      },
      onError: () => failPreview(),
    })
  }

  return (
    <Dialog open onOpenChange={handleCancel}>
      <FormProvider {...form}>
        <DialogContentCustom
          variant="large"
          className="grid h-225 max-h-[90vh] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0"
          onPointerDownOutside={(event) => event.preventDefault()}
          data-testid="record-extract-dialog">
          <DialogHeader className="border-b">
            <DialogTitle className="mb-0 pb-0 [&>div]:pb-0">{t("patient.recordExtract.title")}</DialogTitle>
            <p className="text-on-surface-subdued -mt-1 px-6 pb-3 text-sm" data-testid="record-extract-subtitle">
              {subtitle}
            </p>
          </DialogHeader>

          <div className="grid grid-rows-[auto_minmax(0,1fr)] gap-8 px-6 py-4">
            <div className="flex flex-col gap-2">
              <Banner type="inline" variant="info">
                {t("patient.recordExtract.deliveryNotice")}
              </Banner>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_763px] gap-4">
              <div className="flex min-h-0 flex-col">
                <div className="flex flex-1 flex-col gap-8 overflow-auto px-0.5">
                  <ExportScopeChips disabled={previewStatus === PREVIEW_STATUS.generating} />
                  {isCustomScope && (
                    <ExtractFilters
                      episodeOfCareOptions={episodeOfCareOptions}
                      disabled={previewStatus === PREVIEW_STATUS.generating}
                    />
                  )}
                </div>
                <div className="flex w-full flex-col gap-4 pt-2">
                  <p className="bg-border h-px w-full" />
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={submitGeneratePreview}
                    disabled={hasNoContact}
                    isLoading={previewStatus === PREVIEW_STATUS.generating}
                    data-testid="record-extract-generate-preview">
                    {t("patient.recordExtract.generatePreview")}
                  </Button>
                </div>
              </div>

              <div className="flex min-h-0 flex-col gap-4">
                {displayStatus === PREVIEW_STATUS.stale && (
                  <Banner type="inline" variant="warning" data-testid="record-extract-stale-banner">
                    {t("patient.recordExtract.staleNotice")}
                  </Banner>
                )}
                <ExtractPreviewPane
                  status={displayStatus}
                  stage={stage}
                  summaryFileId={summaryFileId}
                  errorMessageKey={errorMessageKey}
                  onRetry={submitGeneratePreview}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="border-border bg-surface border-t">
            <Button variant="secondary" onClick={handleCancel} data-testid="record-extract-cancel">
              {t("common.button.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={displayStatus !== PREVIEW_STATUS.loaded}
              isLoading={isConfirming}
              onClick={handleExportAndSend}
              data-testid="record-extract-export-send">
              {t("patient.recordExtract.exportAndSend")}
            </Button>
          </DialogFooter>
        </DialogContentCustom>
      </FormProvider>
    </Dialog>
  )
}
